import { useEffect } from 'react'

declare global {
	interface Window {
		_paq?: Array<unknown[]>
	}
}

/**
 * Push a custom event to Matomo's _paq queue.
 * Silently no-ops when Matomo is not loaded (e.g. dev environment).
 */
export function trackEvent(
	category: string,
	action: string,
	name?: string,
	value?: number,
) {
	if (typeof window === 'undefined') return
	if (!window._paq) return
	const args: Array<string | number | undefined> = [
		'trackEvent',
		category,
		action,
	]
	if (name !== undefined) args.push(name)
	if (value !== undefined) args.push(value)
	window._paq.push(args)
}

/**
 * Delegated click listener that tracks external link clicks.
 * Covers http(s), mailto, and the /shop path (hosted externally).
 */
export function useExternalLinkTracking() {
	useEffect(() => {
		function handleClick(e: MouseEvent) {
			const anchor = (e.target as Element)?.closest?.(
				'a[href]',
			) as HTMLAnchorElement | null
			if (!anchor) return
			const href = anchor.getAttribute('href')
			if (!href) return

			let destination: string | null = null

			if (href.startsWith('http://') || href.startsWith('https://')) {
				try {
					destination = new URL(href).hostname.replace(/^www\./, '')
				} catch {
					destination = href
				}
			} else if (href.startsWith('mailto:')) {
				destination = href.replace('mailto:', '')
			} else if (href === '/shop') {
				destination = 'shop'
			}

			if (destination) {
				trackEvent('external', 'click', destination)
			}
		}
		document.addEventListener('click', handleClick)
		return () => document.removeEventListener('click', handleClick)
	}, [])
}
